import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import {
  Calendar,
  Download,
  Search,
  Phone,
  Clock,
  RefreshCw,
  Loader2,
  ChevronDown,
  ArrowUpDown,
  Check,
  User,
} from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../../components/EmptyState';

export default function Appointments() {
  const { id } = useParams();
  const [appointments, setAppointments] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('upcoming');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [openActionDropdown, setOpenActionDropdown] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which appointment is being updated
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});

  const sortOptions = [
    { value: 'upcoming', label: 'Upcoming First' },
    { value: 'recent', label: 'Most Recent' },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' },
  ];

  const fetchAppointments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getAppointments(id, {
        search: searchQuery,
        status: statusFilter,
        sort: sortBy
      });
      setAppointments(response.appointments || []);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      // Treat 404 as "no data" - endpoint might not be implemented yet
      const is404 = err.message?.includes('404') || err.message?.includes('Not Found') || err.status === 404;
      if (is404) {
        setAppointments([]);
      } else {
        setError('Failed to fetch');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, searchQuery, statusFilter, sortBy]);

  useEffect(() => {
    fetchAppointments();
  }, [id, statusFilter, sortBy]); // Only auto-fetch on filter/sort changes, not search

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Fetch when searchQuery changes (after button click)
  useEffect(() => {
    if (searchQuery !== undefined) {
      fetchAppointments();
    }
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openActionDropdown && !e.target.closest('.status-dropdown') && !e.target.closest('.status-dropdown-portal')) {
        setOpenActionDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionDropdown]);

  // Handle opening dropdown with position calculation
  const handleOpenDropdown = (aptId) => {
    if (openActionDropdown === aptId) {
      setOpenActionDropdown(null);
      return;
    }

    const button = buttonRefs.current[aptId];
    if (button) {
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 176, // 176px = w-44 (11rem)
      });
    }
    setOpenActionDropdown(aptId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700',
      no_show: 'bg-orange-100 text-orange-700',
    };
    const labels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No Show',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      setUpdatingStatus(appointmentId);
      setOpenActionDropdown(null);
      await api.updateAppointmentStatus(id, appointmentId, newStatus);
      await fetchAppointments();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err.message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const statusActionOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-700' },
    { value: 'completed', label: 'Completed', color: 'bg-gray-100 text-gray-700' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
    { value: 'no_show', label: 'No Show', color: 'bg-orange-100 text-orange-700' },
  ];

  const exportCSV = () => {
    const headers = ['Customer Name', 'Phone', 'Date', 'Time', 'Duration', 'Status', 'Notes', 'Created'];
    const rows = appointments.map(apt => [
      apt.customerName || '',
      apt.customerPhone ? `'+${apt.customerPhone.replace(/^\+/, '')}` : '',
      apt.date || '',
      apt.time || '',
      `${apt.durationMinutes} min`,
      apt.status || '',
      apt.notes || '',
      apt.createdAt ? new Date(apt.createdAt).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `appointments-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isUpcoming = (apt) => {
    const aptDate = new Date(`${apt.date}T${apt.time}`);
    return aptDate > new Date() && ['pending', 'confirmed'].includes(apt.status);
  };

  // Show full-page loading spinner for initial load
  if (isLoading && appointments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Appointments</h1>
            <p className="text-text-secondary">{appointments.length} total appointments</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAppointments}
            disabled={isLoading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-text-muted ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={exportCSV}
            disabled={appointments.length === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Content */}
      {appointments.length === 0 && !searchQuery && !statusFilter ? (
        <EmptyState
          icon={Calendar}
          title="No appointments yet"
          description="When customers book appointments through your AI assistant, they'll appear here."
        />
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-border p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    placeholder="Search by name or phone..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="input-field pl-10"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>

              {/* Status Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-border rounded-lg
                             hover:border-primary/30 transition-colors text-sm"
                >
                  <span className="font-medium text-text-primary">
                    {statusOptions.find(opt => opt.value === statusFilter)?.label}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-text-muted transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showStatusDropdown && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-elevated border border-border z-50 overflow-hidden animate-slide-up">
                    <div className="p-2">
                      {statusOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setStatusFilter(option.value);
                            setShowStatusDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left rounded-lg text-sm flex items-center justify-between
                                     hover:bg-gray-50 transition-colors
                                     ${statusFilter === option.value ? 'bg-primary/5 text-primary' : 'text-text-primary'}`}
                        >
                          <span>{option.label}</span>
                          {statusFilter === option.value && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-border rounded-lg
                             hover:border-primary/30 transition-colors text-sm"
                >
                  <ArrowUpDown className="w-3.5 h-3.5 text-primary" />
                  <span className="font-medium text-text-primary">
                    {sortOptions.find(opt => opt.value === sortBy)?.label}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-text-muted transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showSortDropdown && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-elevated border border-border z-50 overflow-hidden animate-slide-up">
                    <div className="p-2">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left rounded-lg text-sm flex items-center justify-between
                                     hover:bg-gray-50 transition-colors
                                     ${sortBy === option.value ? 'bg-primary/5 text-primary' : 'text-text-primary'}`}
                        >
                          <span>{option.label}</span>
                          {sortBy === option.value && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Appointments Table */}
          <div className="bg-white rounded-xl border border-border">
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary/5 to-accent/5 border-b border-border">
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Customer</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Date & Time</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Duration</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {appointments.map((apt, index) => (
                    <tr
                      key={apt.id}
                      className="hover:bg-gray-50 transition-colors animate-slide-up"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      {/* Customer */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isUpcoming(apt)
                              ? 'bg-gradient-to-br from-primary/20 to-accent/20'
                              : 'bg-gray-100'
                          }`}>
                            <User className={`w-5 h-5 ${isUpcoming(apt) ? 'text-primary' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">
                              {apt.customerName || 'Unknown'}
                            </p>
                            {apt.customerPhone && (
                              <p className="text-sm text-text-muted flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {apt.customerPhone}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Date & Time */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-text-muted" />
                          <div>
                            <p className="font-medium text-text-primary">{formatDate(apt.date)}</p>
                            <p className="text-sm text-text-muted flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(apt.time)} - {formatTime(apt.endTime)}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Duration */}
                      <td className="px-6 py-4">
                        <span className="text-sm text-text-secondary">
                          {apt.durationMinutes} min
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        {getStatusBadge(apt.status)}
                      </td>

                      {/* Actions - Status Dropdown */}
                      <td className="px-6 py-4">
                        <div className="status-dropdown">
                          <button
                            ref={(el) => buttonRefs.current[apt.id] = el}
                            type="button"
                            onClick={() => handleOpenDropdown(apt.id)}
                            disabled={updatingStatus === apt.id}
                            className="min-w-[140px] px-3 py-2 bg-white border border-border rounded-xl
                                       flex items-center justify-between text-left
                                       hover:border-primary/30 transition-colors disabled:opacity-70"
                          >
                            {updatingStatus === apt.id ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                <span className="text-sm text-text-muted">Updating...</span>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    statusActionOptions.find(s => s.value === apt.status)?.color || 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {statusActionOptions.find(s => s.value === apt.status)?.label || apt.status}
                                  </span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${
                                  openActionDropdown === apt.id ? 'rotate-180' : ''
                                }`} />
                              </>
                            )}
                          </button>

                          {/* Dropdown Menu - Rendered via Portal */}
                          {openActionDropdown === apt.id && createPortal(
                            <div
                              className="status-dropdown-portal fixed z-[9999] w-44 bg-white border border-border rounded-xl shadow-lg overflow-hidden animate-fade-in"
                              style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                              {statusActionOptions.map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleStatusChange(apt.id, option.value)}
                                  disabled={apt.status === option.value}
                                  className={`w-full p-3 flex items-center gap-3 hover:bg-background transition-colors text-left
                                             disabled:opacity-50 disabled:cursor-not-allowed
                                             ${apt.status === option.value ? 'bg-primary/5' : ''}`}
                                >
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${option.color}`}>
                                    {option.label}
                                  </span>
                                  {apt.status === option.value && (
                                    <Check className="w-4 h-4 text-primary ml-auto" />
                                  )}
                                </button>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {appointments.length === 0 && (searchQuery || statusFilter) && (
              <div className="text-center py-12">
                <p className="text-text-muted">No appointments matching your filters</p>
              </div>
            )}
          </div>

          {/* Results count */}
          {appointments.length > 0 && (
            <div className="mt-4 text-sm text-text-muted text-center">
              Showing {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
            </div>
          )}
        </>
      )}
    </div>
  );
}
